'use client';

import { useIntersectionObserver } from '@packages/shared/src/hooks/useIntersectionObserver/useIntersectionObserver';
import { useAtom } from 'jotai';
import { Suspense, useRef } from 'react';
import { useIntl } from 'react-intl';
import { InstallmentCalculatorErrorFallback } from '@packages/installment-calculator/src/fallbacks/InstallmentCalculatorErrorFallback/InstallmentCalculatorErrorFallback.client';
import { InstallmentCalculatorSuspenseFallback } from '@packages/installment-calculator/src/fallbacks/InstallmentCalculatorSuspenseFallback/InstallmentCalculatorSuspenseFallback';
import { LazyCalculator } from '@packages/installment-calculator/src/LazyCalculator/LazyCalculator.client';
import { installmentCollapsed } from '@packages/modules/src/atoms';
import { Card, CardContent, CardHeader } from '@packages/shared/src/ui/Card/Card';
import { ErrorBoundary } from '@packages/shared/src/ui/ErrorBoundary/ErrorBoundary';

export type InstallmentCardProps = {
  /**
   * Product price value
   */
  basePrice: number;
  /**
   * id of the card -> needed if you want to scroll to the card
   */
  cardID: string;
};

export const InstallmentCard = ({ basePrice, cardID }: InstallmentCardProps) => {
  const calcElement = useRef<HTMLDivElement>(null);
  const { formatMessage } = useIntl();
  const [collapsed] = useAtom(installmentCollapsed);
  const { isIntersecting } = useIntersectionObserver(calcElement, {
    intersectOnce: true,
    threshold: 0,
  });

  return (
    <Card id={cardID} ref={calcElement}>
      <CardHeader hasDivider>
        {formatMessage({
          id: 'installment-calculator.card-headline',
          defaultMessage: 'Ratenrechner',
        })}
      </CardHeader>
      <CardContent>
        {isIntersecting && (
          <ErrorBoundary
            fallback={(reset) => <InstallmentCalculatorErrorFallback onCloseHandler={reset} />}
          >
            <Suspense
              fallback={<InstallmentCalculatorSuspenseFallback calculatorLayout="Collapsible" />}
            >
              <LazyCalculator basePrice={basePrice} layout="Collapsible" collapsed={collapsed} />
            </Suspense>
          </ErrorBoundary>
        )}
      </CardContent>
    </Card>
  );
};
